import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hrApi } from '@/api';
import { EmployeeSelect } from '@/components/selects';
import { PageActionGate } from '@/components/auth/PageActionGate';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { FormActions } from '@/components/ui/FormActions';
import { HrHierarchyTree } from '@/modules/hr/components/HrHierarchyTree';
import type { HrHierarchyNode } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function HrOrgStructurePage() {
  const navigate = useNavigate();
  const [hierarchy, setHierarchy] = useState<HrHierarchyNode[]>([]);
  const [assignEmployeeId, setAssignEmployeeId] = useState('');
  const [assignManagerId, setAssignManagerId] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const tree = await hrApi.hierarchyTree();
      setHierarchy(tree);
    } catch (err) {
      showApiError(err, 'Failed to load reporting hierarchy');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveReporting = async () => {
    if (!assignEmployeeId || !assignManagerId) {
      notify.error('Select employee and manager');
      return;
    }
    if (assignEmployeeId === assignManagerId) {
      notify.error('Employee and manager must be different');
      return;
    }
    try {
      await hrApi.assignManager(Number(assignEmployeeId), Number(assignManagerId));
      notify.success('Reporting relationship saved');
      setAssignEmployeeId('');
      setAssignManagerId('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to assign manager');
    }
  };

  return (
    <div>
      <PageHeader
        title="Reporting hierarchy"
        description="View and manage who reports to whom. Departments, designations, grades, and business units are configured in HR master data."
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'HR', to: '/hr' }, { label: 'Reporting hierarchy' }]}
        actions={
          <Button variant="secondary" onClick={() => navigate('/hr/master-data?tab=businessUnits')}>
            Org master data
          </Button>
        }
      />
      {loading && <p className="mb-4 text-sm text-slate-500">Loading…</p>}

      <section className="card p-4">
        <h3 className="mb-1 font-semibold">Assign manager</h3>
        <p className="mb-4 text-sm text-slate-500">
          Set or change an employee&apos;s direct manager. The tree below reflects current reporting lines.
        </p>
        <div className="mb-6 grid gap-3 sm:grid-cols-2">
          <EmployeeSelect label="Employee" value={assignEmployeeId} onChange={setAssignEmployeeId} hrScoped />
          <EmployeeSelect label="Manager" value={assignManagerId} onChange={setAssignManagerId} hrScoped />
        </div>
        <PageActionGate action="edit">
          <FormActions
            primaryLabel="Assign manager"
            onPrimary={() => void saveReporting()}
            onReset={() => {
              setAssignEmployeeId('');
              setAssignManagerId('');
            }}
            bordered={false}
          />
        </PageActionGate>
        <HrHierarchyTree
          nodes={hierarchy}
          onSelect={(id) => navigate(`/hr/employees/${id}`)}
          defaultExpanded
        />
      </section>
    </div>
  );
}
